import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ChevronDown, ChevronUp, ImagePlus, Paperclip, User, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { collectReferencesFromMarkdown } from "@/lib/contentReferences";
import {
  MEDIA_POST_MAX_IMAGES,
  MEDIA_POST_MAX_VIDEOS,
  POST_ATTACHMENT_MAX_COUNT,
  firstVideoEmbedFromText,
  imageFilesFromPaste,
  isImageFile,
  isVideoFile,
  parseVideoEmbedUrl,
} from "@/lib/parseVideoEmbed";
import { teamFileRefsToFiles } from "@/lib/postAttachments";
import { toastError } from "@/lib/toast";
import { fetchUserMe } from "@/lib/userMeCache";
import { API_BASE_URL } from "@/lib/apiBase";
import { useAuth } from "@/scripts/AuthContext";
import { useSocialProfileOptional } from "@/scripts/SocialProfileContext";
import { useIsMobile } from "@/hooks/use-mobile";
import EmojiInsertButton from "./EmojiInsertButton";
import MediaAttachBar from "./MediaAttachBar";
import MediaCarousel from "./MediaCarousel";
import PhotoOrderEditor from "./PhotoOrderEditor";
import DmAttachPickerDialog from "./dm/DmAttachPickerDialog";
import OrgMemberAvatar from "@/components/avatars/OrgMemberAvatar";
import SocialUserAvatar from "@/components/avatars/SocialUserAvatar";
import RichMarkdownEditor, {
  isMarkdownEmpty,
  normalizeEditorMarkdown,
} from "./RichMarkdownEditor";

/** Sync default for destination picker (avoid empty key until useEffect runs). */
function defaultDestinationKey(destinations, initialDestinationKey) {
  if (!Array.isArray(destinations) || destinations.length === 0) return "";
  if (
    initialDestinationKey &&
    destinations.some((d) => `${d.type}:${d.id}` === initialDestinationKey)
  ) {
    return initialDestinationKey;
  }
  const firstTeam = destinations.find((d) => d.type === "team");
  const pick = firstTeam || destinations[0];
  return `${pick.type}:${pick.id}`;
}

/**
 * Shared post composer for dashboard (collapsed/expanded) and team Feed (fixed team, optional hideDestination).
 *
 * destinations: [{ type: 'team'|'org', id: string|number, label: string }]
 *
 * Drafts: pass onSaveDraft for discard/save-draft dialog (dashboard/team).
 * Profile resume: pass autosave + onSaveDraft (debounced PATCH) and use submitLabel="Publish".
 */
const PostComposer = ({
  mode = "collapsed",
  destinationMode = "picker",
  destinations = [],
  orgId,
  fixedTeamId,
  fixedTeamLabel,
  hideDestination = false,
  isSubmitting = false,
  onSubmit,
  onSaveDraft,
  /** When true with onSaveDraft, debounce-save instead of showing Save draft */
  autosave = false,
  className,
  initialTitle = "",
  initialContent = "",
  initialTags = "",
  initialCritical = false,
  /** Prefill destination select: "team:123" or "org:uuid" */
  initialDestinationKey = "",
  submitLabel = "Post",
  formKey = 0,
  embedded = false,
  /** false | true | (destination) => boolean — who may set Critical on compose */
  allowCritical = false,
  /** Hide file attach UI (e.g. Community Space for now) */
  hideAttachments = false,
  /** Hide tags field (e.g. Community Space for now) */
  hideTags = false,
  /** Prefill a pending YouTube/Vimeo embed url (e.g. editing a draft) */
  initialEmbedUrl = "",
  /** Already-uploaded media on a post/draft being edited: [{ type, url, ... }] */
  initialMedia = [],
  /** async (index) => void — remove an already-uploaded media item while editing */
  onRemoveMedia,
  /** Post composer always allows a single video (embed or upload) alongside images */
  allowVideoEmbed = true,
  allowVideoUpload = true,
  /**
   * When false (Circa Social), title UI stays optional. Workplace also allows
   * empty titles (tweet-style); this flag only affects labeling/layout quirks.
   */
  inferTitle = true,
  /** Override body editor placeholder (Circa Social Home). */
  contentPlaceholder = "Share an update…",
}) => {
  const { accessToken } = useAuth();
  const socialProfileCtx = useSocialProfileOptional();
  const socialProfile = socialProfileCtx?.profile || null;
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(mode === "expanded");
  const [title, setTitle] = useState(initialTitle);
  const [content, setContent] = useState(initialContent);
  const [tags, setTags] = useState(initialTags);
  const [critical, setCritical] = useState(Boolean(initialCritical));
  const [destinationKey, setDestinationKey] = useState(() =>
    destinationMode === "picker"
      ? defaultDestinationKey(destinations, initialDestinationKey)
      : ""
  );
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [embedUrl, setEmbedUrl] = useState(initialEmbedUrl || "");
  /** Cleared via chip; does not strip the URL from the body */
  const [dismissedEmbedUrl, setDismissedEmbedUrl] = useState("");
  const [existingMedia, setExistingMedia] = useState(initialMedia || []);
  const [removingMediaIndex, setRemovingMediaIndex] = useState(null);
  const [orderEditorOpen, setOrderEditorOpen] = useState(false);
  const [attachPickerOpen, setAttachPickerOpen] = useState(false);
  const [attachingTeamFiles, setAttachingTeamFiles] = useState(false);
  const [viewerUser, setViewerUser] = useState(null);
  const editorRef = useRef(null);
  const mediaAttachRef = useRef(null);
  const autosaveReadyRef = useRef(false);
  const skipAutosaveRef = useRef(false);
  const submitInFlightRef = useRef(false);

  const isSocialComposer = inferTitle === false;

  /** Prefer live TipTap markdown at submit/save time if React state lags. */
  const getLiveContent = () => {
    const fromEditor = editorRef.current?.getMarkdown?.();
    if (typeof fromEditor === "string") {
      if (!isMarkdownEmpty(fromEditor) || isMarkdownEmpty(content)) {
        return fromEditor;
      }
    }
    return content;
  };

  useEffect(() => {
    if (!accessToken || isSocialComposer) {
      setViewerUser(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchUserMe(accessToken);
        if (cancelled || !data) return;
        const u = data.user || data;
        const userId = u?.user_id || u?.userId || data.user_id || data.userId;
        let memberAvatar =
          data.member_avatar_url ||
          data.memberAvatarUrl ||
          u?.member_avatar_url ||
          null;
        // Org PFPs live on membership, not /user/me — hydrate when org-scoped.
        if (orgId && userId) {
          try {
            const { data: profile } = await axios.get(
              `${API_BASE_URL}/org/${encodeURIComponent(orgId)}/members/${encodeURIComponent(userId)}/profile`,
              { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            if (profile?.member_avatar_url) {
              memberAvatar = profile.member_avatar_url;
            }
          } catch {
            // Keep initials fallback if profile/avatar is unavailable.
          }
        }
        if (cancelled) return;
        setViewerUser(
          u
            ? {
                ...u,
                user_id: userId,
                member_avatar_url: memberAvatar || null,
              }
            : null
        );
      } catch {
        if (!cancelled) setViewerUser(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, isSocialComposer, orgId]);

  const hasPendingVideoFile = useMemo(
    () => pendingFiles.some((f) => isVideoFile(f)),
    [pendingFiles]
  );

  const pendingImageFiles = useMemo(
    () => pendingFiles.filter((f) => isImageFile(f) && !isVideoFile(f)),
    [pendingFiles]
  );

  const inferredEmbed = useMemo(
    () => (allowVideoEmbed ? firstVideoEmbedFromText(content) : null),
    [content, allowVideoEmbed]
  );

  const embedMeta = useMemo(
    () => (embedUrl ? parseVideoEmbedUrl(embedUrl) : null),
    [embedUrl]
  );

  const referenceContext = useMemo(
    () =>
      orgId && accessToken
        ? {
            enabled: true,
            orgId,
            accessToken,
            contextType: "post",
          }
        : null,
    [orgId, accessToken]
  );

  useEffect(() => {
    skipAutosaveRef.current = true;
    setTitle(initialTitle);
    setContent(initialContent);
    setTags(initialTags);
    setCritical(Boolean(initialCritical));
    setPendingFiles([]);
    setEmbedUrl(initialEmbedUrl || "");
    setDismissedEmbedUrl("");
    setExistingMedia(initialMedia || []);
    setOrderEditorOpen(false);
    if (mode !== "expanded") {
      setExpanded(false);
    }
    autosaveReadyRef.current = false;
    const t = window.setTimeout(() => {
      skipAutosaveRef.current = false;
      autosaveReadyRef.current = true;
    }, 50);
    return () => window.clearTimeout(t);
  }, [formKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mode === "expanded") {
      setExpanded(true);
    }
  }, [mode]);

  useEffect(() => {
    if (destinationMode === "fixed") {
      return;
    }
    if (!destinations.length) {
      setDestinationKey("");
      return;
    }
    setDestinationKey((prev) => {
      if (prev && destinations.some((d) => `${d.type}:${d.id}` === prev)) {
        return prev;
      }
      if (
        initialDestinationKey &&
        destinations.some((d) => `${d.type}:${d.id}` === initialDestinationKey)
      ) {
        return initialDestinationKey;
      }
      const firstTeam = destinations.find((d) => d.type === "team");
      const pick = firstTeam || destinations[0];
      return `${pick.type}:${pick.id}`;
    });
  }, [destinations, destinationMode, initialDestinationKey, formKey]);

  const selectedDestination =
    destinationMode === "fixed"
      ? { type: "team", id: fixedTeamId, label: fixedTeamLabel || "Team" }
      : destinations.find((d) => `${d.type}:${d.id}` === destinationKey) ||
        destinations[0] ||
        undefined;

  // Keep controlled select value aligned if state briefly lags destinations.
  const resolvedDestinationKey =
    destinationMode === "fixed"
      ? destinationKey
      : selectedDestination
        ? `${selectedDestination.type}:${selectedDestination.id}`
        : destinationKey;

  useEffect(() => {
    if (destinationMode !== "picker") return;
    if (!destinations.length) return;
    const selected =
      destinations.find((d) => `${d.type}:${d.id}` === destinationKey) ||
      destinations[0];
    if (!selected) return;
    const key = `${selected.type}:${selected.id}`;
    if (key !== destinationKey) {
      setDestinationKey(key);
    }
  }, [destinationMode, destinations, destinationKey]);

  /** Workplace org compose uses the message file picker; Social stays photos. */
  const workplaceFileAttach =
    Boolean(orgId) && inferTitle && !hideAttachments;
  const attachTeamId =
    selectedDestination?.type === "team" &&
    Number.isFinite(Number(selectedDestination.id))
      ? Number(selectedDestination.id)
      : null;
  const remainingAttach = Math.max(
    0,
    POST_ATTACHMENT_MAX_COUNT - pendingFiles.length
  );

  const criticalAllowed =
    typeof allowCritical === "function"
      ? Boolean(allowCritical(selectedDestination))
      : Boolean(allowCritical);

  useEffect(() => {
    if (!criticalAllowed) setCritical(false);
  }, [criticalAllowed]);

  // Infer YouTube/Vimeo embed from body (XOR with uploaded video file).
  useEffect(() => {
    if (hideAttachments || !allowVideoEmbed) return;
    if (hasPendingVideoFile) {
      setEmbedUrl("");
      return;
    }
    if (!inferredEmbed) {
      setEmbedUrl("");
      return;
    }
    if (dismissedEmbedUrl && dismissedEmbedUrl === inferredEmbed.url) {
      return;
    }
    setEmbedUrl(inferredEmbed.url);
  }, [
    inferredEmbed,
    hasPendingVideoFile,
    hideAttachments,
    allowVideoEmbed,
    dismissedEmbedUrl,
  ]);

  const clearInferredEmbed = () => {
    setDismissedEmbedUrl(embedUrl || inferredEmbed?.url || "");
    setEmbedUrl("");
  };

  const applyPhotoOrder = (reorderedImages) => {
    const video = pendingFiles.find((f) => isVideoFile(f));
    setPendingFiles(video ? [...reorderedImages, video] : reorderedImages);
  };

  const handleExpand = () => {
    setExpanded(true);
  };

  const handleCollapse = () => {
    if (mode === "expanded") return;
    setExpanded(false);
  };

  const buildPayload = ({ allowEmpty = false } = {}) => {
    const body = normalizeEditorMarkdown(getLiveContent());
    const emptyBody = isMarkdownEmpty(body);
    if (!allowEmpty && emptyBody) return null;

    const trimmedTitle = title.trim();
    // Silent titles: never invent a title from body ("Update" / first line).
    const resolvedTitle = trimmedTitle;

    if (allowEmpty && emptyBody && !resolvedTitle) {
      return null;
    }

    const payload = {
      title: resolvedTitle || "",
      content: emptyBody ? "" : body,
      tags: hideTags ? undefined : tags.trim() || undefined,
      critical: criticalAllowed ? Boolean(critical) : false,
      org_id: orgId,
      references: collectReferencesFromMarkdown(
        emptyBody ? "" : body,
        orgId
      ),
      files: hideAttachments ? [] : pendingFiles,
      embedUrl: hideAttachments || hasPendingVideoFile
        ? ""
        : dismissedEmbedUrl &&
            inferredEmbed &&
            dismissedEmbedUrl === inferredEmbed.url
          ? ""
          : embedUrl.trim() || inferredEmbed?.url || "",
    };

    if (destinationMode === "fixed") {
      payload.team_id =
        typeof fixedTeamId === "string"
          ? parseInt(fixedTeamId, 10)
          : fixedTeamId;
    } else if (selectedDestination?.type === "team") {
      payload.team_id =
        typeof selectedDestination.id === "string"
          ? parseInt(selectedDestination.id, 10)
          : selectedDestination.id;
    } else if (selectedDestination?.type === "org") {
      payload.team_id = undefined;
      payload.org_id = selectedDestination.id || orgId;
    }

    return payload;
  };

  const resetComposerLocal = () => {
    setContent("");
    setTitle("");
    setTags("");
    setCritical(false);
    setPendingFiles([]);
    setEmbedUrl("");
    setDismissedEmbedUrl("");
    if (mode !== "expanded") setExpanded(false);
    editorRef.current?.clear();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (isSubmitting || isSavingDraft || submitInFlightRef.current) return;
    if (destinationMode === "picker" && !selectedDestination) return;

    const payload = buildPayload({ allowEmpty: false });
    if (!payload) return;

    submitInFlightRef.current = true;
    try {
      await onSubmit?.(payload);
      // Parent bumps formKey on success; also clear locally so the TipTap
      // surface empties immediately even while still focused.
      resetComposerLocal();
    } catch {
      // Parent shows the error; keep the draft.
    } finally {
      submitInFlightRef.current = false;
    }
  };

  const handleSaveDraft = async (e) => {
    e?.preventDefault?.();
    if (!onSaveDraft || isSubmitting || isSavingDraft) return;
    if (destinationMode === "picker" && !selectedDestination) return;

    const payload = buildPayload({ allowEmpty: true });
    if (!payload) return;
    if (!payload.title.trim() && isMarkdownEmpty(payload.content)) return;

    setIsSavingDraft(true);
    try {
      await onSaveDraft(payload);
      resetComposerLocal();
      setDiscardDialogOpen(false);
    } catch {
      // Parent shows the error; keep content.
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleDiscardLocal = () => {
    resetComposerLocal();
    setDiscardDialogOpen(false);
  };

  const removeExistingMedia = async (index) => {
    if (!onRemoveMedia) return;
    setRemovingMediaIndex(index);
    try {
      await onRemoveMedia(index);
      setExistingMedia((prev) => prev.filter((_, i) => i !== index));
    } catch {
      // Parent toasts.
    } finally {
      setRemovingMediaIndex(null);
    }
  };

  const pendingImageCount = pendingImageFiles.length;
  const pendingVideoCount = useMemo(
    () => pendingFiles.filter((f) => isVideoFile(f)).length,
    [pendingFiles]
  );
  const photoAttachDisabled = workplaceFileAttach
    ? isSubmitting ||
      isSavingDraft ||
      attachingTeamFiles ||
      pendingFiles.length >= POST_ATTACHMENT_MAX_COUNT
    : isSubmitting ||
      isSavingDraft ||
      (pendingImageCount >= MEDIA_POST_MAX_IMAGES &&
        (pendingVideoCount > 0 ||
          Boolean(embedUrl.trim()) ||
          MEDIA_POST_MAX_VIDEOS <= 0));

  const attachControl = (
    <div className="space-y-2">
      {existingMedia.length ? (
        <div className="space-y-1">
          <MediaCarousel items={existingMedia} className="max-w-sm" />
          {onRemoveMedia ? (
            <div className="flex flex-wrap gap-1.5">
              {existingMedia.map((m, i) => (
                <Button
                  key={`${m.type}-${i}`}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-6 px-2 text-[11px] text-muted-foreground"
                  disabled={removingMediaIndex != null || isSubmitting || isSavingDraft}
                  onClick={() => removeExistingMedia(i)}
                >
                  <X className="h-3 w-3 mr-1" />
                  {removingMediaIndex === i ? "Removing…" : `Remove ${m.type === "image" ? "image" : "media"} ${i + 1}`}
                </Button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      <MediaAttachBar
        ref={mediaAttachRef}
        mode={workplaceFileAttach ? "files" : "post"}
        pendingFiles={pendingFiles}
        onFilesChange={setPendingFiles}
        onRequestOrderEditor={() => setOrderEditorOpen(true)}
        hasPendingVideoEmbed={Boolean(embedUrl.trim())}
        allowVideoUpload={allowVideoUpload}
        showAttachButton={false}
        disabled={isSubmitting || isSavingDraft || attachingTeamFiles}
      />
      <PhotoOrderEditor
        open={orderEditorOpen}
        onOpenChange={setOrderEditorOpen}
        files={pendingImageFiles}
        onConfirm={applyPhotoOrder}
      />
    </div>
  );

  const appendPickedFiles = (files) => {
    mediaAttachRef.current?.appendFiles?.(files);
  };

  const handlePickTeamFiles = async (refs) => {
    if (!refs?.length || !orgId || !accessToken) return;
    setAttachingTeamFiles(true);
    try {
      const files = await teamFileRefsToFiles({
        accessToken,
        orgId,
        refs,
      });
      appendPickedFiles(files);
    } catch (err) {
      toastError(err, "Could not attach Team Files");
    } finally {
      setAttachingTeamFiles(false);
    }
  };

  const onEditorPaste = (e) => {
    if (hideAttachments) return;
    if (workplaceFileAttach) {
      const items = (e.nativeEvent || e)?.clipboardData?.items;
      if (!items?.length) return;
      const files = [];
      for (const item of items) {
        if (item.kind !== "file") continue;
        const f = item.getAsFile();
        if (f) files.push(f);
      }
      if (!files.length) return;
      e.preventDefault?.();
      appendPickedFiles(files);
      return;
    }
    const images = imageFilesFromPaste(e.nativeEvent || e);
    if (!images.length) return;
    setPendingFiles((prev) => {
      const next = [...prev, ...images].slice(0, MEDIA_POST_MAX_IMAGES);
      const nextImageCount = next.filter(
        (f) => isImageFile(f) && !isVideoFile(f)
      ).length;
      if (nextImageCount >= 2) {
        queueMicrotask(() => setOrderEditorOpen(true));
      }
      return next;
    });
  };

  const onFormDragOver = (e) => {
    if (!workplaceFileAttach) return;
    const types = Array.from(e.dataTransfer?.types || []);
    if (!types.includes("Files")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const onFormDrop = (e) => {
    if (!workplaceFileAttach) return;
    const files = e.dataTransfer?.files;
    if (!files?.length) return;
    e.preventDefault();
    appendPickedFiles(Array.from(files));
  };

  // Profile resume: debounced autosave (no clear of title/content)
  useEffect(() => {
    if (!autosave || !onSaveDraft) return;
    if (!autosaveReadyRef.current || skipAutosaveRef.current) return;
    if (destinationMode === "picker" && !selectedDestination) return;

    const timer = window.setTimeout(async () => {
      const payload = buildPayload({ allowEmpty: true });
      if (!payload) return;
      if (
        !payload.title.trim() &&
        isMarkdownEmpty(payload.content) &&
        !(payload.files && payload.files.length) &&
        !payload.embedUrl
      ) {
        return;
      }
      try {
        await onSaveDraft(payload, { autosave: true });
        if (payload.files?.length) {
          setPendingFiles([]);
        }
        if (payload.embedUrl) {
          setEmbedUrl("");
        }
      } catch {
        /* parent toasts */
      }
    }, 1000);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    title,
    content,
    tags,
    critical,
    destinationKey,
    pendingFiles,
    embedUrl,
    autosave,
    selectedDestination,
  ]);

  const bodyIsEmpty = isMarkdownEmpty(content);

  const canSubmit =
    !bodyIsEmpty &&
    !isSubmitting &&
    !isSavingDraft &&
    (destinationMode === "fixed" || Boolean(selectedDestination));

  const canSaveDraft =
    Boolean(onSaveDraft) &&
    !autosave &&
    !isSubmitting &&
    !isSavingDraft &&
    (destinationMode === "fixed" || Boolean(selectedDestination)) &&
    (Boolean(title.trim()) || !bodyIsEmpty);

  const showDiscardControl = Boolean(onSaveDraft) && !autosave;
  const hasComposableContent =
    Boolean(title.trim()) ||
    !bodyIsEmpty ||
    pendingFiles.length > 0 ||
    Boolean(embedUrl.trim()) ||
    existingMedia.length > 0;

  const discardControl =
    showDiscardControl ? (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0 text-muted-foreground"
        disabled={isSubmitting || isSavingDraft || !hasComposableContent}
        onClick={() => setDiscardDialogOpen(true)}
        aria-label="Discard"
        title="Discard"
      >
        <X className="h-4 w-4" />
      </Button>
    ) : null;

  const discardDialog = (
    <Dialog open={discardDialogOpen} onOpenChange={setDiscardDialogOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Discard this post?</DialogTitle>
          <DialogDescription>
            Save it as a draft to finish later, or discard and clear the
            composer.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => setDiscardDialogOpen(false)}
            disabled={isSavingDraft}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="text-destructive hover:text-destructive"
            onClick={handleDiscardLocal}
            disabled={isSavingDraft}
          >
            Discard
          </Button>
          <Button
            type="button"
            disabled={!canSaveDraft || isSavingDraft}
            onClick={handleSaveDraft}
          >
            {isSavingDraft ? "Saving…" : "Save draft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const destinationSelectCompact =
    hideDestination || destinationMode !== "picker" ? null : (
      <select
        id="post-destination"
        aria-label="Post to"
        className="h-8 max-w-[9.5rem] shrink-0 truncate rounded-full border border-border/70 bg-muted/40 px-2.5 text-xs text-foreground"
        value={resolvedDestinationKey}
        onChange={(e) => setDestinationKey(e.target.value)}
        disabled={isSubmitting || isSavingDraft || destinations.length === 0}
      >
        {destinations.length === 0 ? (
          <option value="">No destinations</option>
        ) : (
          destinations.map((d) => (
            <option key={`${d.type}:${d.id}`} value={`${d.type}:${d.id}`}>
              {d.label}
            </option>
          ))
        )}
      </select>
    );

  const destinationSelect = hideDestination
    ? null
    : destinationMode === "picker" ? (
      <div className="space-y-1.5">
        <Label htmlFor="post-destination-expanded" className="text-xs text-muted-foreground">
          Post to
        </Label>
        <select
          id="post-destination-expanded"
          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
          value={resolvedDestinationKey}
          onChange={(e) => setDestinationKey(e.target.value)}
          disabled={isSubmitting || isSavingDraft || destinations.length === 0}
        >
          {destinations.length === 0 ? (
            <option value="">No destinations available</option>
          ) : (
            destinations.map((d) => (
              <option key={`${d.type}:${d.id}`} value={`${d.type}:${d.id}`}>
                {d.label}
              </option>
            ))
          )}
        </select>
      </div>
    ) : (
      <p className="text-sm text-muted-foreground">
        Posting to <span className="font-medium text-foreground">{fixedTeamLabel}</span>
      </p>
    );

  const bodyEditorCollapsed = (
    <div className="min-w-0 flex-1" onPaste={onEditorPaste}>
      <RichMarkdownEditor
        key={`post-body-${formKey}-collapsed`}
        ref={editorRef}
        value={content}
        onChange={setContent}
        onSubmit={handleSubmit}
        submitMode="mod-enter"
        placeholder={contentPlaceholder}
        disabled={isSubmitting || isSavingDraft}
        minRows={1}
        toolbar="never"
        variant="compact"
        referenceContext={referenceContext}
        className="border-0 shadow-none bg-transparent [&_.ProseMirror]:min-h-[1.5rem] [&_.ProseMirror]:py-1.5 [&_.ProseMirror]:px-0"
      />
    </div>
  );

  const bodyEditor = (
    <div className="space-y-1.5">
      {expanded ? <Label>Message</Label> : null}
      <div onPaste={onEditorPaste}>
        <RichMarkdownEditor
          key={`post-body-${formKey}-${expanded ? "expanded" : "collapsed"}`}
          ref={editorRef}
          value={content}
          onChange={setContent}
          onSubmit={handleSubmit}
          submitMode="mod-enter"
          placeholder={contentPlaceholder}
          disabled={isSubmitting || isSavingDraft}
          minRows={expanded ? 6 : isMobile ? 2 : 3}
          referenceContext={referenceContext}
        />
      </div>
      {!hideAttachments && embedUrl.trim() && embedMeta ? (
        <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-2 py-1.5">
          <span className="text-xs text-muted-foreground">
            Embed:{" "}
            <span className="font-medium text-foreground">
              {embedMeta.provider === "vimeo" ? "Vimeo" : "YouTube"}
            </span>
          </span>
          <button
            type="button"
            className="ml-auto inline-flex h-5 w-5 items-center justify-center rounded-full hover:text-destructive"
            onClick={clearInferredEmbed}
            disabled={isSubmitting || isSavingDraft}
            aria-label="Clear embed"
            title="Clear embed (link stays in the post)"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : null}
    </div>
  );

  const photoAttachButton = !hideAttachments ? (
    workplaceFileAttach ? (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0"
        onClick={() => setAttachPickerOpen(true)}
        disabled={photoAttachDisabled}
        title="Attach files"
        aria-label="Attach files"
      >
        <Paperclip className="h-4 w-4" />
      </Button>
    ) : (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0"
        onClick={() => mediaAttachRef.current?.openPicker?.()}
        disabled={photoAttachDisabled}
        title={`Add photos (up to ${MEDIA_POST_MAX_IMAGES}) or a video`}
        aria-label="Add photos or a video"
      >
        <ImagePlus className="h-4 w-4" />
      </Button>
    )
  ) : null;

  const formInner = (body) =>
    embedded ? (
      <div className={cn("space-y-4 py-2", className)}>{body}</div>
    ) : (
      <Card className={cn("border-border", className)}>
        <CardContent className="pt-4 pb-4 space-y-4">{body}</CardContent>
      </Card>
    );

  const attachPicker =
    workplaceFileAttach ? (
      <DmAttachPickerDialog
        open={attachPickerOpen}
        onOpenChange={setAttachPickerOpen}
        accessToken={accessToken}
        orgId={orgId}
        channelTeamId={attachTeamId}
        remaining={remainingAttach}
        onPickLocalFiles={appendPickedFiles}
        onPickTeamFiles={handlePickTeamFiles}
      />
    ) : null;

  if (!expanded) {
    return (
      <>
        <div className={cn("border-b border-border/50 pb-3", className)}>
          <form
            onSubmit={handleSubmit}
            className="space-y-2"
            onDragOver={onFormDragOver}
            onDrop={onFormDrop}
          >
            <div className="flex items-start gap-2.5 min-w-0">
              <div className="mt-1 shrink-0">
                {isSocialComposer && socialProfile ? (
                  <SocialUserAvatar
                    user={socialProfile}
                    avatarUrl={socialProfile.avatar_url}
                    displayName={socialProfile.display_name}
                    className="h-9 w-9"
                    fallbackClassName="text-xs"
                  />
                ) : viewerUser ? (
                  <OrgMemberAvatar
                    user={viewerUser}
                    className="h-9 w-9"
                    fallbackClassName="text-xs"
                  />
                ) : (
                  <div
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-muted-foreground"
                    aria-hidden
                  >
                    <User className="h-4 w-4" />
                  </div>
                )}
              </div>
              {bodyEditorCollapsed}
              <Button
                type="submit"
                size="sm"
                className="mt-0.5 shrink-0 rounded-full px-4"
                disabled={!canSubmit}
              >
                {isSubmitting ? "…" : submitLabel}
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pl-[2.875rem]">
              {destinationSelectCompact}
              <div className="flex flex-nowrap items-center gap-0.5">
                <EmojiInsertButton
                  onInsert={(native) => editorRef.current?.insertText(native)}
                  disabled={isSubmitting || isSavingDraft}
                />
                {photoAttachButton}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 shrink-0 text-xs text-muted-foreground"
                  onClick={handleExpand}
                  disabled={isSubmitting || isSavingDraft}
                >
                  <span className="hidden sm:inline">More options</span>
                  <span className="sm:hidden">More</span>
                  <ChevronDown className="h-3.5 w-3.5 ml-1" />
                </Button>
                {discardControl}
              </div>
            </div>
            {!hideAttachments && embedUrl.trim() && embedMeta ? (
              <div className="ml-[2.875rem] flex items-center gap-2 rounded-md bg-muted/30 px-2 py-1.5">
                <span className="text-xs text-muted-foreground">
                  Embed:{" "}
                  <span className="font-medium text-foreground">
                    {embedMeta.provider === "vimeo" ? "Vimeo" : "YouTube"}
                  </span>
                </span>
                <button
                  type="button"
                  className="ml-auto inline-flex h-5 w-5 items-center justify-center rounded-full hover:text-destructive"
                  onClick={clearInferredEmbed}
                  disabled={isSubmitting || isSavingDraft}
                  aria-label="Clear embed"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : null}
            {!hideAttachments ? (
              <div className="ml-[2.875rem]">{attachControl}</div>
            ) : null}
          </form>
        </div>
        {attachPicker}
        {discardDialog}
      </>
    );
  }

  return (
    <>
      {formInner(
        <form
          onSubmit={handleSubmit}
          className="space-y-4"
          onDragOver={onFormDragOver}
          onDrop={onFormDrop}
        >
          {destinationSelect}

          <div className="space-y-1.5">
            <Label htmlFor="post-title">
              Title (optional)
            </Label>
            <Input
              id="post-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={
                "Optional title"
              }
              disabled={isSubmitting || isSavingDraft}
            />
          </div>

          {bodyEditor}

          {!hideAttachments ? attachControl : null}

          {!hideTags ? (
            <div className="space-y-1.5">
              <Label htmlFor="post-tags">Tags</Label>
              <Input
                id="post-tags"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="Optional, comma-separated"
                disabled={isSubmitting || isSavingDraft}
              />
            </div>
          ) : null}

          {criticalAllowed && (
            <div className="flex items-start gap-2">
              <input
                id="post-critical"
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-input"
                checked={critical}
                onChange={(e) => setCritical(e.target.checked)}
                disabled={isSubmitting || isSavingDraft}
              />
              <Label
                htmlFor="post-critical"
                className="text-sm font-normal leading-snug cursor-pointer"
              >
                Critical for 24 hours — stays at the top of the feed and notifies
                the audience
              </Label>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2">
            {mode !== "expanded" ? (
              <div className="flex min-w-0 flex-nowrap items-center gap-0.5">
                <EmojiInsertButton
                  onInsert={(native) => editorRef.current?.insertText(native)}
                  disabled={isSubmitting || isSavingDraft}
                />
                {photoAttachButton}
                {discardControl}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 shrink-0 text-xs text-muted-foreground"
                  onClick={handleCollapse}
                  disabled={isSubmitting || isSavingDraft}
                >
                  Collapse
                  <ChevronUp className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            ) : (
              <div className="flex flex-nowrap items-center gap-0.5">
                <EmojiInsertButton
                  onInsert={(native) => editorRef.current?.insertText(native)}
                  disabled={isSubmitting || isSavingDraft}
                />
                {photoAttachButton}
                {discardControl}
              </div>
            )}
            <div className="flex items-center gap-2">
              <Button type="submit" disabled={!canSubmit}>
                {isSubmitting ? "Posting…" : submitLabel}
              </Button>
            </div>
          </div>
        </form>
      )}
      {attachPicker}
      {discardDialog}
    </>
  );
};

export default PostComposer;
